import { useEffect, useMemo, useState } from 'react';
import { App, Button, Input, Modal, Select } from 'antd';
import { ArrowRightOutlined, CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { formatCredits } from '../../../data/home';
import { getDepositInfo } from '../../Account/format';
import { AppApc } from '../../../api/appApc';

type TopUpStep = 'form' | 'confirm';

type TopUpModalProps = {
    open: boolean;
    onClose: () => void;
};

function parseUsdt(value: string) {
    const text = value.trim();

    if (text === '' || text === '.') {
        return null;
    }

    if (!/^\d*\.?\d*$/.test(text)) {
        return null;
    }

    const amount = Number(text);
    if (!Number.isFinite(amount) || amount < 0) {
        return null;
    }

    return amount;
}

function sanitizeUsdtInput(value: string) {
    const next = value.replace(/[^\d.]/g, '');
    const dot = next.indexOf('.');

    if (dot === -1) {
        return next;
    }

    return `${next.slice(0, dot + 1)}${next.slice(dot + 1).replace(/\./g, '')}`;
}

function shortenAddress(address: string, start = 10, end = 8) {
    if (address.length <= start + end + 3) {
        return address;
    }

    return `${address.slice(0, start)}...${address.slice(-end)}`;
}

function TopUpModal({ open, onClose }: TopUpModalProps) {
    const { t } = useTranslation('home');
    const { message } = App.useApp();
    const { user } = useAuth();
    const [dataDepositOption, setDataDepositOption] = useState<any>(null);
    const [depositOptionLoading, setDepositOptionLoading] = useState(false);
    const [step, setStep] = useState<TopUpStep>('form');
    const [amount, setAmount] = useState('0');
    const [network, setNetwork] = useState('bsc');
    const parsedAmount = useMemo(() => parseUsdt(amount), [amount]);
    const networkLabel = dataDepositOption?.depositAddress?.network;
    const deposit = useMemo(() => (user ? getDepositInfo(user) : { address: '', qrPayload: '' }), [user]);
    const qrValue = dataDepositOption?.depositAddress?.qrPayload || deposit.address;

    const depositOption = async () => {
        setDepositOptionLoading(true);
        try {
            const response = await AppApc.GetDepositOption();
            if(response.status === 200) {
                setDataDepositOption(response?.data?.data);
            }
        } catch (error) {
            console.error("Error fetching deposit option" , error);
        } finally {
            setDepositOptionLoading(false);
        }
    }

    useEffect(() => {
        if (open) {
            depositOption();
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            setStep('form');
            setAmount('0');
            setNetwork('bsc');
        }
    }, [open]);

    const onContinue = () => {
        if (parsedAmount === null || parsedAmount <= 0) {
            message.error(t('topUp.messages.invalidAmount'));
            return;
        }

        setStep('confirm');
    };

    const onCopy = async () => {
        const address = dataDepositOption?.depositAddress?.address;
        if (!address) {
            return;
        }

        try {
            await navigator.clipboard.writeText(address);
            message.success(t('topUp.messages.addressCopied'));
        } catch {
            message.error(t('topUp.messages.copyFailed'));
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={510}
            centered
            className="topup-modal"
            destroyOnClose
        >
            <div className="topup-modal__head">
                <h2>{step === 'confirm' ? t('topUp.confirmTitle') : t('topUp.title')}</h2>
                <Button
                    type="text"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    aria-label={t('topUp.a11y.close')}
                    className="topup-modal__close"
                />
            </div>

            {step === 'form' ? (
                <>
                    <p className="topup-modal__lead">{t('topUp.lead')}</p>

                    <div className="topup-modal__rate">
                        <span>{t('topUp.fixedConversion')}</span>
                        <strong>{t('topUp.rate')}</strong>
                    </div>

                    <label className="topup-modal__label">{t('topUp.depositNetwork')}</label>
                    <Select
                        className="topup-modal__select"
                        popupClassName="topup-modal__dropdown"
                        value={network}
                        onChange={setNetwork}
                        options={[{ value: 'bsc', label: t('topUp.bscNetwork') }]}
                    />

                    <label className="topup-modal__label">{t('topUp.youDeposit')}</label>
                    <Input
                        className="topup-modal__input"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={t('topUp.amountPlaceholder')}
                        value={amount}
                        onChange={(event) => setAmount(sanitizeUsdtInput(event.target.value))}
                    />

                    <div className="topup-modal__receive">
                        <span>{t('topUp.youReceive')}</span>
                        <strong>
                            {t('topUp.creditAmount', {
                                amount: formatCredits(parsedAmount ?? 0),
                                count: parsedAmount ?? 0,
                            })}
                        </strong>
                    </div>
                    <p className="topup-modal__note">
                        {t('topUp.automaticNote')}
                    </p>

                    <Button
                        type="primary"
                        className="topup-modal__submit"
                        disabled={
                            depositOptionLoading ||
                            parsedAmount === null ||
                            parsedAmount <= 0
                        }
                        loading={depositOptionLoading}
                        onClick={onContinue}
                    >
                        {t('topUp.continue')}
                        <ArrowRightOutlined />
                    </Button>
                </>
            ) : (
                <>
                    <p className="topup-modal__lead">
                        {t('topUp.confirmLead')}
                    </p>

                    <div className="topup-modal__rate">
                        <span>{t('topUp.amountToSend')}</span>
                        <strong>
                            {formatCredits(parsedAmount ?? 0)} USDT
                        </strong>
                    </div>

                    <div className="topup-modal__meta">
                        <div>
                            <span>{t('topUp.network')}</span>
                            <strong>{networkLabel}</strong>
                        </div>
                        <div>
                            <span>{t('topUp.youReceive')}</span>
                            <strong>{t('topUp.creditAmount', {
                                amount: formatCredits(parsedAmount ?? 0),
                                count: parsedAmount ?? 0,
                            })}</strong>
                        </div>
                    </div>

                    {qrValue ? (
                        <div className="topup-modal__qr">
                            <QRCodeSVG
                                value={qrValue}
                                size={168}
                                marginSize={2}
                                bgColor="#eaf5fd"
                                fgColor="#081321"
                                level="M"
                                title={t('topUp.a11y.depositQr')}
                            />
                        </div>
                    ) : (
                        <div className="topup-modal__empty">
                            {t('topUp.noAddress')}
                        </div>
                    )}

                    <div className="topup-modal__copy">
                        <span>{t('topUp.depositAddress')}</span>
                        <div className="topup-modal__copy-row">
                            <code title={dataDepositOption?.depositAddress?.address || undefined}>
                                {dataDepositOption?.depositAddress?.address ? shortenAddress(dataDepositOption?.depositAddress?.address) : '—'}
                            </code>
                            <Button
                                icon={<CopyOutlined />}
                                onClick={onCopy}
                                disabled={!dataDepositOption?.depositAddress?.address}
                            >
                                {t('topUp.copy')}
                            </Button>
                        </div>
                    </div>

                    <p className="topup-modal__note">
                        {t('topUp.networkNote', { network: networkLabel })}
                    </p>

                    <Button type="primary" className="topup-modal__submit" onClick={onClose}>
                        {t('topUp.done')}
                    </Button>
                    <button
                        type="button"
                        className="text-button topup-modal__back"
                        onClick={() => setStep('form')}
                    >
                        {t('topUp.changeAmount')}
                    </button>
                </>
            )}
        </Modal>
    );
}

export default TopUpModal;
